import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import {
  MdAdd,
  MdArchive,
  MdAttachFile,
  MdCalendarToday,
  MdChatBubbleOutline,
  MdCheck,
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdClose,
  MdColorLens,
  MdEdit,
  MdFileCopy,
  MdGroup,
  MdImage,
  MdLabel,
  MdMoreHoriz,
  MdOpenWith,
  MdSubject,
} from "react-icons/md";
import { supabase } from "../../../../lib/supabaseClient";
import { addTaskUpdate, fetchTaskUpdates, fetchTasks } from "../../../../hooks/useProjects";

const FONT = '"Atlassian Sans", ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const FONT_STACK = { fontFamily: FONT };

const SURFACE = "#ffffff";
const SUNKEN = "#f1f2f4";
const TEXT = "#172b4d";
const MUTED = "#44546f";
const SUBTLE = "#626f86";
const BORDER = "#dfe1e6";
const BTN_BG = "#091e420f";
const BTN_HOVER = "#091e4224";
const SHADOW_CARD = "0 1px 1px rgba(0, 0, 0, 0.1)";
const SHADOW_CARD_HOVER = "0 4px 12px rgba(0, 0, 0, 0.15)";
const SHADOW_DRAG = "0 12px 24px rgba(9, 30, 66, 0.2), 0 0 1px rgba(9, 30, 66, 0.31)";
const SHADOW_MODAL = "0 20px 40px rgba(9, 30, 66, 0.3)";
const PRIMARY = "#3761E9";

const COLUMN_THEMES = [
  {
    id: "todo",
    label: "To Do",
    bg: "#1a1a2e",
    text: "#ffffff",
    badgeText: "rgba(255,255,255,0.9)",
  },
  {
    id: "in_progress",
    label: "In Progress",
    bg: "#FFF7D6",
    text: "#172b4d",
    badgeText: "#7F5F01",
  },
  {
    id: "review",
    label: "Review",
    bg: "#FFF0E0",
    text: "#172b4d",
    badgeText: "#A54800",
  },
  {
    id: "testing",
    label: "Testing",
    bg: "#DCFFF1",
    text: "#172b4d",
    badgeText: "#216E4E",
  },
  {
    id: "done",
    label: "Done",
    bg: "#E9F2FF",
    text: "#172b4d",
    badgeText: "#0055CC",
  },
  {
    id: "extra_teal",
    label: "Extra",
    bg: "#e3fcef",
    text: "#206a83",
    badgeText: "#206A83",
  },
  {
    id: "extra_pink",
    label: "Extra",
    bg: "#ffecf8",
    text: "#943d73",
    badgeText: "#943D73",
  },
];

const DEFAULT_COLUMNS = COLUMN_THEMES.slice(0, 5).map(({ id, label, bg, text, badgeText }) => ({
  id,
  label,
  bg,
  text,
  badgeText,
}));

const getColumnLabelsStorageKey = (projectId) => `taskboard_column_labels_${projectId}`;

function applyStoredColumnLabels(projectId, columns) {
  if (!projectId || typeof window === "undefined") return columns;

  try {
    const raw = window.localStorage.getItem(getColumnLabelsStorageKey(projectId));
    if (!raw) return columns;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return columns;

    return columns.map((column) => ({
      ...column,
      label: typeof parsed[column.id] === "string" && parsed[column.id].trim() ? parsed[column.id].trim() : column.label,
    }));
  } catch {
    return columns;
  }
}

function persistStoredColumnLabels(projectId, columns) {
  if (!projectId || typeof window === "undefined") return;

  try {
    const payload = Object.fromEntries(columns.map((column) => [column.id, column.label]));
    window.localStorage.setItem(getColumnLabelsStorageKey(projectId), JSON.stringify(payload));
  } catch {
    // Ignore storage failures and keep the UI functional.
  }
}

const LABEL_COLORS = [
  { id: "green", value: "#4bce97", dark: "#216e4e" },
  { id: "yellow", value: "#f5cd47", dark: "#7f5f01" },
  { id: "orange", value: "#fea362", dark: "#a54800" },
  { id: "red", value: "#f87168", dark: "#ae2e24" },
  { id: "pink", value: "#e774bb", dark: "#943d73" },
  { id: "purple", value: "#9f8fef", dark: "#5e4db2" },
  { id: "blue", value: "#579dff", dark: "#0055cc" },
  { id: "sky", value: "#6cc3e0", dark: "#206a83" },
  { id: "lime", value: "#94c748", dark: "#5b7f24" },
  { id: "slate", value: "#8590a2", dark: "#44546f" },
];

const COVER_COLORS = [
  "#4BCE97",
  "#F5CD47",
  "#FEA362",
  "#F87168",
  "#9F8FEF",
  "#579DFF",
];

const SECTION_LABEL_STYLE = {
  ...FONT_STACK,
  fontSize: "12px",
  fontWeight: 600,
  color: MUTED,
  margin: 0,
};

const makeId = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const sortByPosition = (a, b) => (a.position || 0) - (b.position || 0);
const asArray = (value) => (Array.isArray(value) ? value : []);

function normalizeTask(task) {
  if (!task) return null;
  return {
    ...task,
    labels: asArray(task.labels),
    checklist: asArray(task.checklist),
    member_ids: asArray(task.member_ids),
    attachments: asArray(task.attachments),
    card_color: task.card_color && task.card_color !== "#FFFFFF" ? task.card_color : null,
    is_archived: Boolean(task.is_archived),
    position: Number.isFinite(task.position) ? task.position : 0,
  };
}

function prettyColumnLabel(columnId) {
  return String(columnId || "Untitled")
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function mergeColumnsWithTasks(currentColumns, tasks) {
  const nextColumns = [...currentColumns];
  const seen = new Set(nextColumns.map((column) => column.id));

  tasks.forEach((task) => {
    const columnId = task.column_id || "todo";
    if (!seen.has(columnId)) {
      const theme = COLUMN_THEMES[nextColumns.length % COLUMN_THEMES.length];
      nextColumns.push({
        id: columnId,
        label: prettyColumnLabel(columnId),
        bg: theme.bg,
        text: theme.text,
        badgeText: theme.badgeText,
      });
      seen.add(columnId);
    }
  });

  return nextColumns;
}

function buildColumnMap(columns, tasks) {
  const map = {};
  columns.forEach((column) => {
    map[column.id] = [];
  });

  tasks.forEach((task) => {
    const columnId = task.column_id || "todo";
    if (!map[columnId]) {
      map[columnId] = [];
    }
    map[columnId].push(task);
  });

  Object.values(map).forEach((list) => list.sort(sortByPosition));
  return map;
}

function reorderBoardTasks(tasks, source, destination) {
  const clonedTasks = tasks.map((task) => ({ ...task }));
  const grouped = {};

  clonedTasks.forEach((task) => {
    const columnId = task.column_id || "todo";
    if (!grouped[columnId]) {
      grouped[columnId] = [];
    }
    grouped[columnId].push(task);
  });

  Object.values(grouped).forEach((list) => list.sort(sortByPosition));

  const sourceList = [...(grouped[source.droppableId] || [])];
  const [movedTask] = sourceList.splice(source.index, 1);
  if (!movedTask) return null;

  if (source.droppableId === destination.droppableId) {
    sourceList.splice(destination.index, 0, movedTask);
    grouped[source.droppableId] = sourceList;
  } else {
    const destinationList = [...(grouped[destination.droppableId] || [])];
    movedTask.column_id = destination.droppableId;
    destinationList.splice(destination.index, 0, movedTask);
    grouped[source.droppableId] = sourceList;
    grouped[destination.droppableId] = destinationList;
  }

  const changed = [];
  Object.entries(grouped).forEach(([columnId, list]) => {
    list.forEach((task, index) => {
      const didChange = task.column_id !== columnId || task.position !== index;
      task.column_id = columnId;
      task.position = index;
      if (didChange) {
        changed.push({ id: task.id, column_id: columnId, position: index });
      }
    });
  });

  return {
    changed,
    nextTasks: clonedTasks.map((task) => normalizeTask(task)),
  };
}

function relTime(value) {
  if (!value) return "";
  const diffMs = Date.now() - new Date(value).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(value).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

function formatDate(value) {
  if (!value) return "";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
}

function isOverdue(value) {
  if (!value) return false;
  const date = new Date(value);
  const now = new Date();
  return date < now && date.toDateString() !== now.toDateString();
}

function initials(name) {
  return (name || "?")
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function autoSize(element) {
  if (!element) return;
  element.style.height = "auto";
  element.style.height = `${element.scrollHeight}px`;
}

function MemberAvatar({ member, size = 28 }) {
  return (
    <div
      title={member?.full_name || "Member"}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: "999px",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#dfe1e6",
        color: MUTED,
        fontSize: `${Math.max(10, size * 0.35)}px`,
        fontWeight: 700,
        flexShrink: 0,
        ...FONT_STACK,
      }}
    >
      {member?.avatar_url ? (
        <img src={member.avatar_url} alt={member.full_name || "Member avatar"} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        initials(member?.full_name)
      )}
    </div>
  );
}

function SidebarButton({ icon, label, active, onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "8px 12px",
        border: "none",
        borderRadius: "8px",
        background: active ? BTN_HOVER : hovered ? BTN_HOVER : BTN_BG,
        color: TEXT,
        fontSize: "14px",
        fontWeight: 600,
        lineHeight: 1.2,
        cursor: "pointer",
        textAlign: "left",
        transition: "background 120ms ease",
        ...FONT_STACK,
      }}
    >
      {React.cloneElement(icon, { size: 18 })}
      <span>{label}</span>
    </button>
  );
}

function SidebarPanel({ title, onClose, children }) {
  return (
    <div
      style={{
        background: SURFACE,
        borderRadius: "12px",
        border: `1px solid ${BORDER}`,
        boxShadow: SHADOW_CARD,
        padding: "12px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
        <p style={{ ...FONT_STACK, fontSize: "12px", fontWeight: 700, color: MUTED, margin: 0, textTransform: "uppercase", letterSpacing: "0.04em" }}>{title}</p>
        <button
          type="button"
          onClick={onClose}
          style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer", color: MUTED, lineHeight: 0 }}
        >
          <MdClose size={18} />
        </button>
      </div>
      {children}
    </div>
  );
}

function TaskCard({ task, index, onOpenTask, teamMembers, columnId, columns, isLast, onDeleteCard, onCopyCard, onMoveCard }) {
  const [hovered, setHovered] = useState(false);
  const labels = task.labels || [];
  const checklist = task.checklist || [];
  const attachments = task.attachments || [];
  const memberIds = task.member_ids || [];
  const dueDate = task.due_date;
  const completedItems = checklist.filter((item) => item.done).length;
  const memberMap = useMemo(() => new Map((teamMembers || []).map((member) => [member.id, member])), [teamMembers]);
  const isDarkCard = columnId === "todo";
  const cardBackground = isDarkCard ? (hovered ? "#323246" : "#242436") : hovered ? "#f8f9fa" : SURFACE;
  const titleColor = isDarkCard ? "#ffffff" : TEXT;
  const descriptionColor = isDarkCard ? "rgba(255,255,255,0.7)" : SUBTLE;
  const metaBackground = isDarkCard ? "rgba(255,255,255,0.12)" : SUNKEN;
  const metaColor = isDarkCard ? "rgba(255,255,255,0.85)" : MUTED;
  const avatarBorder = isDarkCard ? "#242436" : "#fff";
  const contentPadding = "10px 12px 8px";
  const cardBorder = isDarkCard ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid transparent";
  const menuButtonBg = isDarkCard ? "rgba(255, 255, 255, 0.12)" : "rgba(255, 255, 255, 0.92)";
  const menuButtonColor = isDarkCard ? "#ffffff" : MUTED;

  return (
    <Draggable draggableId={task.id} index={index}>
      {(provided, snapshot) => (
        <article
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={() => onOpenTask(task.id)}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            position: "relative",
            overflow: "hidden",
            borderRadius: "8px",
            background: cardBackground,
            border: cardBorder,
            boxShadow: isDarkCard
              ? snapshot.isDragging
                ? SHADOW_DRAG
                : hovered
                  ? "0 4px 12px rgba(0, 0, 0, 0.28)"
                  : "0 1px 1px rgba(0, 0, 0, 0.2)"
              : snapshot.isDragging
                ? SHADOW_DRAG
                : hovered
                  ? SHADOW_CARD_HOVER
                  : SHADOW_CARD,
            cursor: "pointer",
            marginBottom: isLast ? 0 : "8px",
            height: "auto",
            flexShrink: 0,
            transform: snapshot.isDragging ? "rotate(1.5deg)" : "none",
            transition: "box-shadow 120ms ease, transform 120ms ease, background 120ms ease, border-color 120ms ease",
            ...provided.draggableProps.style,
          }}
        >
          <div style={{ padding: contentPadding }}>
            {labels.length > 0 && (
              <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", marginBottom: "8px" }}>
                {labels.map((labelId) => {
                  const match = LABEL_COLORS.find((item) => item.id === labelId);
                  if (!match) return null;
                  return <span key={labelId} style={{ width: "40px", height: "6px", borderRadius: "999px", background: match.value }} />;
                })}
              </div>
            )}

            <p style={{ ...FONT_STACK, color: titleColor, fontSize: "13px", lineHeight: 1.35, margin: 0, fontWeight: 500, wordBreak: "break-word" }}>
              {task.title}
            </p>

            {task.description && (
              <p
                style={{
                  ...FONT_STACK,
                  color: descriptionColor,
                  fontSize: "11px",
                  lineHeight: 1.4,
                  margin: "4px 0 0",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {task.description}
              </p>
            )}

            {(dueDate || checklist.length > 0 || attachments.length > 0 || memberIds.length > 0) && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", marginTop: "8px" }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                  {dueDate && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "2px 6px",
                        borderRadius: "6px",
                        background: isDarkCard ? "rgba(255,255,255,0.12)" : isOverdue(dueDate) ? "#ffebe6" : SUNKEN,
                        color: isDarkCard ? "rgba(255,255,255,0.85)" : isOverdue(dueDate) ? "#ae2e24" : MUTED,
                        fontSize: "10px",
                        fontWeight: 600,
                        ...FONT_STACK,
                      }}
                    >
                      <MdCalendarToday size={10} />
                      {formatDate(dueDate)}
                    </span>
                  )}
                  {checklist.length > 0 && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "2px 6px",
                        borderRadius: "6px",
                        background: isDarkCard ? "rgba(255,255,255,0.12)" : completedItems === checklist.length ? "#dcfff1" : SUNKEN,
                        color: isDarkCard ? "rgba(255,255,255,0.85)" : completedItems === checklist.length ? "#216e4e" : MUTED,
                        fontSize: "10px",
                        fontWeight: 600,
                        ...FONT_STACK,
                      }}
                    >
                      <MdCheckBox size={10} />
                      {completedItems}/{checklist.length}
                    </span>
                  )}
                  {attachments.length > 0 && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "2px 6px",
                        borderRadius: "6px",
                        background: metaBackground,
                        color: metaColor,
                        fontSize: "10px",
                        fontWeight: 600,
                        ...FONT_STACK,
                      }}
                    >
                      <MdAttachFile size={10} />
                      {attachments.length}
                    </span>
                  )}
                </div>

                {memberIds.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: "-4px" }}>
                    {memberIds.slice(0, 3).map((memberId) => {
                      const member = memberMap.get(memberId);
                      return (
                        <div key={memberId} style={{ marginLeft: "-6px", border: `2px solid ${avatarBorder}`, borderRadius: "999px" }}>
                          <MemberAvatar member={member || { full_name: "Member" }} size={22} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {(hovered) && !snapshot.isDragging && (
            <>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenTask(task.id);
                }}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "10px",
                  width: "26px",
                  height: "26px",
                  borderRadius: "8px",
                  border: "none",
                  background: menuButtonBg,
                  color: menuButtonColor,
                  boxShadow: SHADOW_CARD,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 0,
                }}
              >
                <MdEdit size={12} />
              </button>
            </>
          )}
        </article>
      )}
    </Draggable>
  );
}

function AddCardForm({ onAdd, onCancel, textColor }) {
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onAdd(title.trim());
      setTitle("");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: "8px" }}>
      <textarea
        ref={inputRef}
        value={title}
        onChange={(event) => {
          setTitle(event.target.value);
          autoSize(event.target);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") onCancel();
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            if (title.trim()) {
              handleSubmit(event);
            }
          }
        }}
        placeholder="Enter a title for this card..."
        rows={3}
        style={{
          width: "100%",
          resize: "none",
          overflow: "hidden",
          border: "none",
          borderRadius: "8px",
          background: "#ffffff",
          padding: "10px 12px",
          boxSizing: "border-box",
          outline: "none",
          boxShadow: `inset 0 0 0 2px ${PRIMARY}`,
          fontSize: "14px",
          lineHeight: 1.45,
          color: TEXT,
          ...FONT_STACK,
        }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
        <button
          type="submit"
          disabled={saving || !title.trim()}
          style={{
            border: "none",
            borderRadius: "8px",
            padding: "8px 14px",
            background: PRIMARY,
            color: "#fff",
            fontSize: "13px",
            fontWeight: 700,
            cursor: saving ? "default" : "pointer",
            opacity: saving || !title.trim() ? 0.6 : 1,
            ...FONT_STACK,
          }}
        >
          {saving ? "Adding..." : "Add card"}
        </button>
        <button type="button" onClick={onCancel} style={{ background: "transparent", border: "none", color: textColor, cursor: "pointer", padding: 0, lineHeight: 0 }}>
          <MdClose size={20} />
        </button>
      </div>
    </form>
  );
}

function AddListForm({ onAdd, onCancel }) {
  const [value, setValue] = useState("");
  const [hovered, setHovered] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (value.trim()) {
          onAdd(value.trim());
          setValue("");
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: "272px",
        minWidth: "272px",
        background: hovered ? "rgba(0,0,0,0.1)" : "rgba(0,0,0,0.05)",
        borderRadius: "12px",
        padding: "12px",
        alignSelf: "flex-start",
      }}
    >
      <input
        ref={inputRef}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") onCancel();
        }}
        placeholder="Enter list title..."
        style={{
          width: "100%",
          borderRadius: "10px",
            border: `2px solid ${PRIMARY}`,
          padding: "10px 12px",
          boxSizing: "border-box",
          fontSize: "14px",
          color: TEXT,
          outline: "none",
          ...FONT_STACK,
        }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "10px" }}>
        <button type="submit" disabled={!value.trim()} style={{ border: "none", borderRadius: "8px", padding: "8px 14px", background: PRIMARY, color: "#fff", fontSize: "13px", fontWeight: 700, cursor: !value.trim() ? "default" : "pointer", opacity: !value.trim() ? 0.6 : 1, ...FONT_STACK }}>
          Add list
        </button>
        <button type="button" onClick={onCancel} style={{ background: "transparent", border: "none", color: "#fff", cursor: "pointer", padding: 0, lineHeight: 0 }}>
          <MdClose size={20} />
        </button>
      </div>
    </form>
  );
}

function TaskModal({
  task,
  columns,
  currentUser,
  taskCountsByColumn,
  onClose,
  onCardUpdate,
  onCardInserted,
  onCardArchived,
}) {
  const [localTask, setLocalTask] = useState(normalizeTask(task));
  const [updates, setUpdates] = useState([]);
  const [loadingUpdates, setLoadingUpdates] = useState(true);
  const [comment, setComment] = useState("");
  const [sendingComment, setSendingComment] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [draftTitle, setDraftTitle] = useState(task.title || "");
  const [editingDescription, setEditingDescription] = useState(false);
  const [draftDescription, setDraftDescription] = useState(task.description || "");
  const [activePanel, setActivePanel] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [checklistDraft, setChecklistDraft] = useState("");
  const [attachmentName, setAttachmentName] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const titleRef = useRef(null);
  const descriptionRef = useRef(null);
  const commentRef = useRef(null);

  useEffect(() => {
    const normalized = normalizeTask(task);
    setLocalTask(normalized);
    setDraftTitle(normalized.title || "");
    setDraftDescription(normalized.description || "");
  }, [task]);

  useEffect(() => {
    if (editingTitle && titleRef.current) {
      autoSize(titleRef.current);
      titleRef.current.focus();
      titleRef.current.select();
    }
  }, [editingTitle]);

  useEffect(() => {
    if (editingDescription && descriptionRef.current) {
      autoSize(descriptionRef.current);
      descriptionRef.current.focus();
    }
  }, [editingDescription]);

  const loadMembers = useCallback(async () => {
    if (loadingMembers || teamMembers.length > 0) return;
    setLoadingMembers(true);
    try {
      const { data, error } = await supabase
        .from("employees")
        .select("id, full_name, avatar_url, job_title")
        .eq("status", "Active")
        .order("full_name");
      if (error) throw error;
      setTeamMembers(data || []);
    } catch (error) {
      alert(error.message);
    } finally {
      setLoadingMembers(false);
    }
  }, [loadingMembers, teamMembers.length]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const loadUpdates = useCallback(async () => {
    setLoadingUpdates(true);
    try {
      const rows = await fetchTaskUpdates(localTask.id);
      setUpdates(rows || []);
    } finally {
      setLoadingUpdates(false);
    }
  }, [localTask.id]);

  useEffect(() => {
    loadUpdates();
  }, [loadUpdates]);

  useEffect(() => {
    const taskChannel = supabase
      .channel(`task_detail_${localTask.id}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "project_tasks", filter: `id=eq.${localTask.id}` },
        (payload) => {
          const nextTask = normalizeTask(payload.new);
          setLocalTask(nextTask);
          setDraftTitle(nextTask.title || "");
          setDraftDescription(nextTask.description || "");
          onCardUpdate(nextTask);
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(taskChannel);
    };
  }, [localTask.id, onCardUpdate]);

  useEffect(() => {
    const updatesChannel = supabase
      .channel(`task_updates_${localTask.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "task_updates", filter: `task_id=eq.${localTask.id}` },
        () => {
          loadUpdates();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(updatesChannel);
    };
  }, [localTask.id, loadUpdates]);

  const persistTask = useCallback(
    async (fields) => {
      const previous = localTask;
      const optimistic = normalizeTask({
        ...localTask,
        ...fields,
        updated_at: new Date().toISOString(),
      });

      setLocalTask(optimistic);
      onCardUpdate(optimistic);

      const { data, error } = await supabase
        .from("project_tasks")
        .update({ ...fields, updated_at: new Date().toISOString() })
        .eq("id", localTask.id)
        .select("*")
        .single();

      if (error) {
        setLocalTask(previous);
        onCardUpdate(previous);
        alert(error.message);
        return null;
      }

      const synced = normalizeTask(data || optimistic);
      setLocalTask(synced);
      onCardUpdate(synced);
      return synced;
    },
    [localTask, onCardUpdate],
  );

  const labels = localTask.labels || [];
  const checklist = localTask.checklist || [];
  const memberIds = localTask.member_ids || [];
  const attachments = localTask.attachments || [];
  const currentColumn = columns.find((column) => column.id === localTask.column_id);
  const completedItems = checklist.filter((item) => item.done).length;
  const checklistProgress = checklist.length ? Math.round((completedItems / checklist.length) * 100) : 0;
  const memberMap = useMemo(() => new Map(teamMembers.map((member) => [member.id, member])), [teamMembers]);

  const togglePanel = (panelName) => {
    setActivePanel((current) => (current === panelName ? null : panelName));
    if (panelName === "members") {
      loadMembers();
    }
  };

  const saveTitle = async () => {
    if (!draftTitle.trim()) return;
    setEditingTitle(false);
    await persistTask({ title: draftTitle.trim() });
  };

  const saveDescription = async () => {
    setEditingDescription(false);
    await persistTask({ description: draftDescription.trim() || null });
  };

  const toggleLabel = async (labelId) => {
    const nextLabels = labels.includes(labelId) ? labels.filter((item) => item !== labelId) : [...labels, labelId];
    await persistTask({ labels: nextLabels });
  };

  const addChecklistItem = async () => {
    if (!checklistDraft.trim()) return;
    const nextChecklist = [
      ...checklist,
      {
        id: makeId("check"),
        text: checklistDraft.trim(),
        done: false,
      },
    ];
    setChecklistDraft("");
    setActivePanel(null);
    await persistTask({ checklist: nextChecklist });
  };

  const toggleChecklistItem = async (itemId) => {
    const nextChecklist = checklist.map((item) => (item.id === itemId ? { ...item, done: !item.done } : item));
    await persistTask({ checklist: nextChecklist });
  };

  const deleteChecklistItem = async (itemId) => {
    await persistTask({ checklist: checklist.filter((item) => item.id !== itemId) });
  };

  const saveDueDate = async (value) => {
    await persistTask({ due_date: value || null });
  };

  const toggleMember = async (memberId) => {
    const nextMembers = memberIds.includes(memberId) ? memberIds.filter((id) => id !== memberId) : [...memberIds, memberId];
    await persistTask({ member_ids: nextMembers });
  };

  const addAttachment = async () => {
    if (!attachmentUrl.trim()) return;
    const url = attachmentUrl.trim();
    const name = attachmentName.trim() || url.replace(/^https?:\/\//, "").split("/")[0] || "Attachment";
    const nextAttachments = [
      ...attachments,
      {
        id: makeId("att"),
        name,
        url,
        created_at: new Date().toISOString(),
      },
    ];
    setAttachmentName("");
    setAttachmentUrl("");
    setActivePanel(null);
    await persistTask({ attachments: nextAttachments });
  };

  const removeAttachment = async (attachmentId) => {
    await persistTask({ attachments: attachments.filter((attachment) => attachment.id !== attachmentId) });
  };

  const selectCover = async (color) => {
    await persistTask({ card_color: color });
  };

  const moveCard = async (columnId) => {
    const nextPosition = taskCountsByColumn[columnId] || 0;
    await persistTask({ column_id: columnId, position: nextPosition });
    setActivePanel(null);
  };

  const copyCard = async () => {
    const payload = {
      project_id: localTask.project_id,
      title: `${localTask.title} (Copy)`,
      description: localTask.description || null,
      column_id: localTask.column_id,
      position: taskCountsByColumn[localTask.column_id] || 0,
      assigned_to: localTask.assigned_to || null,
      created_by: currentUser?.id || localTask.created_by || null,
      labels,
      checklist,
      due_date: localTask.due_date || null,
      member_ids: memberIds,
      attachments,
      card_color: localTask.card_color || null,
      is_archived: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase.from("project_tasks").insert(payload).select("*").single();
    if (error) {
      alert(error.message);
      return;
    }
    onCardInserted(normalizeTask(data));
  };

  const archiveCard = async () => {
    if (!window.confirm(`Delete "${localTask.title}"?`)) return;
    const archivedTask = normalizeTask({ ...localTask, is_archived: true });
    const rollbackTask = normalizeTask(localTask);
    onCardArchived(archivedTask.id);
    onClose();

    const { error } = await supabase
      .from("project_tasks")
      .update({ is_archived: true, updated_at: new Date().toISOString() })
      .eq("id", localTask.id);

    if (error) {
      onCardInserted(rollbackTask);
      alert(error.message);
    }
  };

  const sendComment = async (event) => {
    event.preventDefault();
    if (!comment.trim()) return;
    setSendingComment(true);
    try {
      const entry = await addTaskUpdate({
        taskId: localTask.id,
        employeeId: currentUser?.id,
        content: comment.trim(),
      });
      setUpdates((current) => [...current, entry]);
      setComment("");
      if (commentRef.current) {
        commentRef.current.style.height = "auto";
      }
    } catch (error) {
      alert(error.message);
    } finally {
      setSendingComment(false);
    }
  };

  const hasMeta = labels.length > 0 || memberIds.length > 0 || localTask.due_date || attachments.length > 0;

  return (
    <div
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        padding: "32px",
        background: "rgba(9, 30, 66, 0.54)",
        overflowY: "auto",
        ...FONT_STACK,
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "min(calc(100vw - 64px), 1140px)",
          margin: "0 auto",
          borderRadius: "16px",
          overflow: "hidden",
          background: "#f1f2f4",
          boxShadow: SHADOW_MODAL,
        }}
      >
        {localTask.card_color && (
          <div style={{ position: "relative", height: "160px", background: localTask.card_color }}>
            <div
              style={{
                position: "absolute",
                right: "16px",
                bottom: "16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={() => selectCover(null)}
                style={{
                  border: "none",
                  borderRadius: "6px",
                  background: "rgba(255, 255, 255, 0.85)",
                  padding: "8px 12px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  color: TEXT,
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                  ...FONT_STACK,
                }}
              >
                <MdImage size={16} />
                Remove cover
              </button>
              <button
                type="button"
                onClick={() => togglePanel("cover")}
                style={{
                  border: "none",
                  borderRadius: "6px",
                  background: "rgba(255, 255, 255, 0.85)",
                  padding: "8px 12px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  color: TEXT,
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                  ...FONT_STACK,
                }}
              >
                <MdColorLens size={16} />
                Change cover
              </button>
            </div>
          </div>
        )}

        <div style={{ position: "relative", padding: localTask.card_color ? "20px 24px 28px" : "28px 24px 28px" }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              position: "absolute",
              top: "16px",
              right: "16px",
              width: "32px",
              height: "32px",
              borderRadius: "999px",
              border: "none",
              background: "rgba(9, 30, 66, 0.08)",
              color: MUTED,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 0,
            }}
          >
            <MdClose size={18} />
          </button>

          <div style={{ maxWidth: "620px", paddingRight: "48px" }}>
            {editingTitle ? (
              <textarea
                ref={titleRef}
                value={draftTitle}
                onChange={(event) => {
                  setDraftTitle(event.target.value);
                  autoSize(event.target);
                }}
                onBlur={saveTitle}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    setEditingTitle(false);
                    setDraftTitle(localTask.title || "");
                  }
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    saveTitle();
                  }
                }}
                rows={1}
                style={{
                  width: "100%",
                  resize: "none",
                  overflow: "hidden",
                  borderRadius: "8px",
                  border: `2px solid ${PRIMARY}`,
                  padding: "4px 8px",
                  fontSize: "22px",
                  fontWeight: 700,
                  lineHeight: 1.25,
                  color: TEXT,
                  background: "#fff",
                  outline: "none",
                  boxSizing: "border-box",
                  ...FONT_STACK,
                }}
              />
            ) : (
              <h1
                onClick={() => setEditingTitle(true)}
                style={{
                  margin: 0,
                  padding: "4px 8px",
                  borderRadius: "8px",
                  color: TEXT,
                  fontSize: "22px",
                  fontWeight: 700,
                  lineHeight: 1.25,
                  cursor: "text",
                  wordBreak: "break-word",
                  ...FONT_STACK,
                }}
              >
                {localTask.title}
              </h1>
            )}
            <p style={{ ...FONT_STACK, margin: "8px 0 0 8px", color: SUBTLE, fontSize: "13px" }}>
              in list <span style={{ color: MUTED, fontWeight: 600 }}>{currentColumn?.label || prettyColumnLabel(localTask.column_id)}</span>
            </p>
          </div>

          {hasMeta && (
            <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", marginTop: "20px", paddingLeft: "8px" }}>
              {memberIds.length > 0 && (
                <div>
                  <p style={{ ...SECTION_LABEL_STYLE, marginBottom: "6px" }}>Members</p>
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                    {memberIds.map((memberId) => {
                      const member = memberMap.get(memberId);
                      return <MemberAvatar key={memberId} member={member || { full_name: "Member" }} size={30} />;
                    })}
                  </div>
                </div>
              )}

              {labels.length > 0 && (
                <div>
                  <p style={{ ...SECTION_LABEL_STYLE, marginBottom: "6px" }}>Labels</p>
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                    {labels.map((labelId) => {
                      const color = LABEL_COLORS.find((item) => item.id === labelId);
                      if (!color) return null;
                      return <span key={labelId} style={{ minWidth: "54px", height: "24px", borderRadius: "999px", background: color.value }} />;
                    })}
                  </div>
                </div>
              )}

              {localTask.due_date && (
                <div>
                  <p style={{ ...SECTION_LABEL_STYLE, marginBottom: "6px" }}>Due date</p>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 10px",
                      borderRadius: "8px",
                      background: isOverdue(localTask.due_date) ? "#ffebe6" : SUNKEN,
                      color: isOverdue(localTask.due_date) ? "#ae2e24" : MUTED,
                      fontSize: "13px",
                      fontWeight: 600,
                      ...FONT_STACK,
                    }}
                  >
                    <MdCalendarToday size={14} />
                    {formatDate(localTask.due_date)}
                  </span>
                </div>
              )}

              {attachments.length > 0 && (
                <div>
                  <p style={{ ...SECTION_LABEL_STYLE, marginBottom: "6px" }}>Attachments</p>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "6px 10px", borderRadius: "8px", background: SUNKEN, color: MUTED, fontSize: "13px", fontWeight: 600, ...FONT_STACK }}>
                    <MdAttachFile size={14} />
                    {attachments.length}
                  </span>
                </div>
              )}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 620px) minmax(320px, 460px)", gap: "24px", alignItems: "start", marginTop: "24px" }}>
            <div style={{ minWidth: 0 }}>
              <section style={{ marginBottom: "24px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                  <MdSubject size={18} color={MUTED} />
                  <h2 style={{ ...FONT_STACK, margin: 0, color: TEXT, fontSize: "16px", fontWeight: 700 }}>Description</h2>
                </div>

                {editingDescription ? (
                  <div>
                    <textarea
                      ref={descriptionRef}
                      value={draftDescription}
                      onChange={(event) => {
                        setDraftDescription(event.target.value);
                        autoSize(event.target);
                      }}
                      rows={4}
                      style={{
                        width: "100%",
                        minHeight: "108px",
                        resize: "none",
                        overflow: "hidden",
                        borderRadius: "12px",
                        border: `2px solid ${PRIMARY}`,
                        background: "#fff",
                        padding: "12px 14px",
                        boxSizing: "border-box",
                        outline: "none",
                        color: TEXT,
                        fontSize: "14px",
                        lineHeight: 1.55,
                        ...FONT_STACK,
                      }}
                    />
                    <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                      <button type="button" onClick={saveDescription} style={{ border: "none", borderRadius: "8px", background: PRIMARY, color: "#fff", padding: "8px 14px", fontSize: "13px", fontWeight: 700, cursor: "pointer", ...FONT_STACK }}>
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingDescription(false);
                          setDraftDescription(localTask.description || "");
                        }}
                        style={{ border: "none", borderRadius: "8px", background: BTN_BG, color: TEXT, padding: "8px 14px", fontSize: "13px", fontWeight: 600, cursor: "pointer", ...FONT_STACK }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => setEditingDescription(true)}
                    style={{
                      minHeight: "72px",
                      borderRadius: "12px",
                      background: BTN_BG,
                      padding: "12px 14px",
                      color: localTask.description ? TEXT : SUBTLE,
                      cursor: "text",
                      fontSize: "14px",
                      lineHeight: 1.55,
                      whiteSpace: "pre-wrap",
                      ...FONT_STACK,
                    }}
                  >
                    {localTask.description || "Add a more detailed description..."}
                  </div>
                )}
              </section>

              {attachments.length > 0 && (
                <section style={{ marginBottom: "24px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                    <MdAttachFile size={18} color={MUTED} />
                    <h2 style={{ ...FONT_STACK, margin: 0, color: TEXT, fontSize: "16px", fontWeight: 700 }}>Attachments</h2>
                  </div>
                  <div style={{ display: "grid", gap: "8px" }}>
                    {attachments.map((attachment) => (
                      <div key={attachment.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: "12px", padding: "12px 14px" }}>
                        <div style={{ minWidth: 0 }}>
                          <a href={attachment.url} target="_blank" rel="noreferrer" style={{ ...FONT_STACK, color: PRIMARY, fontSize: "14px", fontWeight: 600, textDecoration: "none", wordBreak: "break-all" }}>
                            {attachment.name}
                          </a>
                          <p style={{ ...FONT_STACK, margin: "4px 0 0", color: SUBTLE, fontSize: "12px", wordBreak: "break-all" }}>{attachment.url}</p>
                        </div>
                        <button type="button" onClick={() => removeAttachment(attachment.id)} style={{ border: "none", background: "transparent", color: MUTED, cursor: "pointer", padding: 0, lineHeight: 0 }}>
                          <MdClose size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {checklist.length > 0 && (
                <section style={{ marginBottom: "24px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                    <MdCheckBox size={18} color={MUTED} />
                    <h2 style={{ ...FONT_STACK, margin: 0, color: TEXT, fontSize: "16px", fontWeight: 700 }}>Checklist</h2>
                    <span style={{ marginLeft: "auto", ...FONT_STACK, color: SUBTLE, fontSize: "12px", fontWeight: 600 }}>{completedItems}/{checklist.length}</span>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                    <span style={{ ...FONT_STACK, width: "34px", textAlign: "right", color: SUBTLE, fontSize: "12px", fontWeight: 600 }}>{checklistProgress}%</span>
                    <div style={{ flex: 1, height: "8px", borderRadius: "999px", background: "#dcdfe4", overflow: "hidden" }}>
                      <div style={{ width: `${checklistProgress}%`, height: "100%", background: checklistProgress === 100 ? "#216e4e" : PRIMARY }} />
                    </div>
                  </div>

                  <div style={{ display: "grid", gap: "6px" }}>
                    {checklist.map((item) => (
                      <div key={item.id} style={{ display: "flex", alignItems: "flex-start", gap: "10px", borderRadius: "10px", padding: "8px 10px", background: checklistProgress === 100 ? "rgba(255,255,255,0.5)" : "transparent" }}>
                        <button type="button" onClick={() => toggleChecklistItem(item.id)} style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer", lineHeight: 0, marginTop: "2px" }}>
                          {item.done ? <MdCheckBox size={18} color={PRIMARY} /> : <MdCheckBoxOutlineBlank size={18} color={MUTED} />}
                        </button>
                        <span style={{ flex: 1, ...FONT_STACK, fontSize: "14px", lineHeight: 1.5, color: item.done ? SUBTLE : TEXT, textDecoration: item.done ? "line-through" : "none" }}>{item.text}</span>
                        <button type="button" onClick={() => deleteChecklistItem(item.id)} style={{ border: "none", background: "transparent", color: SUBTLE, cursor: "pointer", padding: 0, lineHeight: 0 }}>
                          <MdClose size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <section>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                  <MdChatBubbleOutline size={18} color={MUTED} />
                  <h2 style={{ ...FONT_STACK, margin: 0, color: TEXT, fontSize: "16px", fontWeight: 700 }}>Activity</h2>
                </div>

                <form onSubmit={sendComment} style={{ display: "flex", gap: "12px", marginBottom: "18px" }}>
                  <MemberAvatar member={currentUser || { full_name: "You" }} size={32} />
                  <div style={{ flex: 1 }}>
                    <textarea
                      ref={commentRef}
                      value={comment}
                      onChange={(event) => {
                        setComment(event.target.value);
                        autoSize(event.target);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && !event.shiftKey) {
                          event.preventDefault();
                          if (comment.trim()) {
                            event.currentTarget.form?.requestSubmit();
                          }
                        }
                      }}
                      rows={1}
                      placeholder="Write a comment..."
                      style={{
                        width: "100%",
                        resize: "none",
                        overflow: "hidden",
                        minHeight: "40px",
                        borderRadius: "12px",
                        border: `1px solid ${BORDER}`,
                        background: "#fff",
                        padding: "10px 12px",
                        boxSizing: "border-box",
                        outline: "none",
                        color: TEXT,
                        fontSize: "14px",
                        lineHeight: 1.5,
                        ...FONT_STACK,
                      }}
                    />
                    {comment.trim() && (
                      <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                        <button type="submit" disabled={sendingComment} style={{ border: "none", borderRadius: "8px", background: PRIMARY, color: "#fff", padding: "8px 14px", fontSize: "13px", fontWeight: 700, cursor: sendingComment ? "default" : "pointer", opacity: sendingComment ? 0.6 : 1, ...FONT_STACK }}>
                          Save
                        </button>
                        <button type="button" onClick={() => setComment("")} style={{ border: "none", borderRadius: "8px", background: BTN_BG, color: TEXT, padding: "8px 14px", fontSize: "13px", fontWeight: 600, cursor: "pointer", ...FONT_STACK }}>
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </form>

                {loadingUpdates ? (
                  <p style={{ ...FONT_STACK, color: SUBTLE, fontSize: "13px", margin: 0 }}>Loading activity...</p>
                ) : (
                  <div style={{ display: "grid", gap: "14px" }}>
                    {updates.map((entry) => (
                      <div key={entry.id} style={{ display: "flex", gap: "12px" }}>
                        <MemberAvatar member={entry.employees || { full_name: "Unknown" }} size={32} />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "baseline", gap: "8px", marginBottom: "6px", flexWrap: "wrap" }}>
                            <span style={{ ...FONT_STACK, color: TEXT, fontSize: "13px", fontWeight: 700 }}>{entry.employees?.full_name || "Unknown"}</span>
                            <span style={{ ...FONT_STACK, color: SUBTLE, fontSize: "12px" }}>{relTime(entry.created_at)}</span>
                          </div>
                          <div style={{ background: "#fff", borderRadius: "12px", border: `1px solid ${BORDER}`, padding: "10px 12px" }}>
                            <p style={{ ...FONT_STACK, color: TEXT, fontSize: "14px", lineHeight: 1.6, margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{entry.content}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {updates.length === 0 && <p style={{ ...FONT_STACK, color: SUBTLE, fontSize: "13px", margin: 0 }}>No activity yet.</p>}
                  </div>
                )}
              </section>
            </div>

            <aside style={{ width: "100%", maxWidth: "460px", justifySelf: "end" }}>
              <div style={{ display: "grid", gap: "20px" }}>
                <section>
                  <p style={{ ...FONT_STACK, fontSize: "12px", fontWeight: 700, color: MUTED, margin: "0 0 10px", textTransform: "uppercase", letterSpacing: "0.04em" }}>Add to card</p>
                  <div style={{ display: "grid", gap: "8px" }}>
                    <SidebarButton icon={<MdGroup />} label="Members" active={activePanel === "members"} onClick={() => togglePanel("members")} />
                    {activePanel === "members" && (
                      <SidebarPanel title="Members" onClose={() => setActivePanel(null)}>
                        {loadingMembers ? (
                          <p style={{ ...FONT_STACK, fontSize: "13px", color: SUBTLE, margin: 0 }}>Loading members...</p>
                        ) : (
                          <div style={{ display: "grid", gap: "6px" }}>
                            {teamMembers.map((member) => {
                              const selected = memberIds.includes(member.id);
                              return (
                                <button
                                  key={member.id}
                                  type="button"
                                  onClick={() => toggleMember(member.id)}
                                  style={{
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    border: "none",
                                    borderRadius: "10px",
                                    padding: "8px 10px",
                                    background: selected ? "#e9f2ff" : BTN_BG,
                                    cursor: "pointer",
                                    textAlign: "left",
                                  }}
                                >
                                  <MemberAvatar member={member} size={30} />
                                  <div style={{ minWidth: 0, flex: 1 }}>
                                    <p style={{ ...FONT_STACK, color: TEXT, fontSize: "13px", fontWeight: 600, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{member.full_name}</p>
                                    <p style={{ ...FONT_STACK, color: SUBTLE, fontSize: "12px", margin: "2px 0 0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{member.job_title || "Team member"}</p>
                                  </div>
                                  {selected && <MdCheck size={16} color={PRIMARY} />}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </SidebarPanel>
                    )}

                    <SidebarButton icon={<MdLabel />} label="Labels" active={activePanel === "labels"} onClick={() => togglePanel("labels")} />
                    {activePanel === "labels" && (
                      <SidebarPanel title="Labels" onClose={() => setActivePanel(null)}>
                        <div style={{ display: "grid", gap: "6px" }}>
                          {LABEL_COLORS.map((color) => (
                            <button
                              key={color.id}
                              type="button"
                              onClick={() => toggleLabel(color.id)}
                              style={{
                                width: "100%",
                                height: "32px",
                                border: "none",
                                borderRadius: "10px",
                                background: color.value,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                paddingRight: "10px",
                                boxShadow: labels.includes(color.id) ? `0 0 0 2px #fff, 0 0 0 4px ${color.dark}` : "none",
                              }}
                            >
                              {labels.includes(color.id) && <MdCheck size={16} color="#fff" />}
                            </button>
                          ))}
                        </div>
                      </SidebarPanel>
                    )}

                    <SidebarButton icon={<MdCheckBox />} label="Checklist" active={activePanel === "checklist"} onClick={() => togglePanel("checklist")} />
                    {activePanel === "checklist" && (
                      <SidebarPanel title="Checklist" onClose={() => setActivePanel(null)}>
                        <input
                          value={checklistDraft}
                          onChange={(event) => setChecklistDraft(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              addChecklistItem();
                            }
                          }}
                          placeholder="Checklist item"
                          style={{ width: "100%", borderRadius: "10px", border: `1px solid ${BORDER}`, padding: "10px 12px", boxSizing: "border-box", outline: "none", fontSize: "14px", color: TEXT, ...FONT_STACK }}
                        />
                        <button type="button" onClick={addChecklistItem} disabled={!checklistDraft.trim()} style={{ width: "100%", marginTop: "10px", border: "none", borderRadius: "8px", background: PRIMARY, color: "#fff", padding: "8px 12px", fontSize: "13px", fontWeight: 700, cursor: !checklistDraft.trim() ? "default" : "pointer", opacity: !checklistDraft.trim() ? 0.6 : 1, ...FONT_STACK }}>
                          Add checklist item
                        </button>
                      </SidebarPanel>
                    )}

                    <SidebarButton icon={<MdCalendarToday />} label="Dates" active={activePanel === "dates"} onClick={() => togglePanel("dates")} />
                    {activePanel === "dates" && (
                      <SidebarPanel title="Dates" onClose={() => setActivePanel(null)}>
                        <input
                          type="date"
                          value={localTask.due_date || ""}
                          onChange={(event) => saveDueDate(event.target.value)}
                          style={{ width: "100%", borderRadius: "10px", border: `1px solid ${BORDER}`, padding: "10px 12px", boxSizing: "border-box", outline: "none", fontSize: "14px", color: TEXT, ...FONT_STACK }}
                        />
                        {localTask.due_date && (
                          <button type="button" onClick={() => saveDueDate("")} style={{ width: "100%", marginTop: "10px", border: "none", borderRadius: "8px", background: BTN_BG, color: TEXT, padding: "8px 12px", fontSize: "13px", fontWeight: 600, cursor: "pointer", ...FONT_STACK }}>
                            Remove date
                          </button>
                        )}
                      </SidebarPanel>
                    )}

                    <SidebarButton icon={<MdAttachFile />} label="Attachment" active={activePanel === "attachment"} onClick={() => togglePanel("attachment")} />
                    {activePanel === "attachment" && (
                      <SidebarPanel title="Attachment" onClose={() => setActivePanel(null)}>
                        <div style={{ display: "grid", gap: "8px" }}>
                          <input value={attachmentName} onChange={(event) => setAttachmentName(event.target.value)} placeholder="Display name (optional)" style={{ width: "100%", borderRadius: "10px", border: `1px solid ${BORDER}`, padding: "10px 12px", boxSizing: "border-box", outline: "none", fontSize: "14px", color: TEXT, ...FONT_STACK }} />
                          <input value={attachmentUrl} onChange={(event) => setAttachmentUrl(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); addAttachment(); } }} placeholder="https://example.com/file" style={{ width: "100%", borderRadius: "10px", border: `1px solid ${BORDER}`, padding: "10px 12px", boxSizing: "border-box", outline: "none", fontSize: "14px", color: TEXT, ...FONT_STACK }} />
                        </div>
                        <button type="button" onClick={addAttachment} disabled={!attachmentUrl.trim()} style={{ width: "100%", marginTop: "10px", border: "none", borderRadius: "8px", background: PRIMARY, color: "#fff", padding: "8px 12px", fontSize: "13px", fontWeight: 700, cursor: !attachmentUrl.trim() ? "default" : "pointer", opacity: !attachmentUrl.trim() ? 0.6 : 1, ...FONT_STACK }}>
                          Add attachment
                        </button>
                      </SidebarPanel>
                    )}

                    <SidebarButton icon={<MdColorLens />} label="Cover" active={activePanel === "cover"} onClick={() => togglePanel("cover")} />
                    {activePanel === "cover" && (
                      <SidebarPanel title="Cover" onClose={() => setActivePanel(null)}>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: "8px" }}>
                          {COVER_COLORS.map((color) => (
                            <button key={color} type="button" onClick={() => selectCover(color)} style={{ height: "34px", borderRadius: "10px", border: "none", background: color, cursor: "pointer", boxShadow: localTask.card_color === color ? `0 0 0 2px #fff, 0 0 0 4px ${PRIMARY}` : "none" }} />
                          ))}
                          {localTask.card_color && (
                            <button type="button" onClick={() => selectCover(null)} style={{ height: "34px", borderRadius: "10px", border: "none", background: SUNKEN, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: MUTED, lineHeight: 0 }}>
                              <MdClose size={18} />
                            </button>
                          )}
                        </div>
                      </SidebarPanel>
                    )}
                  </div>
                </section>

                <section>
                  <p style={{ ...FONT_STACK, fontSize: "12px", fontWeight: 700, color: MUTED, margin: "0 0 10px", textTransform: "uppercase", letterSpacing: "0.04em" }}>Actions</p>
                  <div style={{ display: "grid", gap: "8px" }}>
                    <SidebarButton icon={<MdOpenWith />} label="Move" active={activePanel === "move"} onClick={() => togglePanel("move")} />
                    {activePanel === "move" && (
                      <SidebarPanel title="Move" onClose={() => setActivePanel(null)}>
                        <div style={{ display: "grid", gap: "6px" }}>
                          {columns.filter((column) => column.id !== localTask.column_id).map((column) => (
                            <button key={column.id} type="button" onClick={() => moveCard(column.id)} style={{ width: "100%", border: "none", borderRadius: "10px", background: BTN_BG, color: TEXT, padding: "10px 12px", textAlign: "left", fontSize: "13px", fontWeight: 600, cursor: "pointer", ...FONT_STACK }}>
                              {column.label}
                            </button>
                          ))}
                        </div>
                      </SidebarPanel>
                    )}

                    <SidebarButton icon={<MdFileCopy />} label="Copy" active={false} onClick={copyCard} />
                    <SidebarButton icon={<MdArchive />} label="Delete" active={false} onClick={archiveCard} />
                  </div>
                </section>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TaskBoard({ project, currentUser }) {
  const [tasks, setTasks] = useState([]);
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [loading, setLoading] = useState(true);
  const [addingCardColumnId, setAddingCardColumnId] = useState(null);
  const [addingList, setAddingList] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [editingColumnId, setEditingColumnId] = useState(null);
  const [columnNameDraft, setColumnNameDraft] = useState("");
  const [columnMenuOpenId, setColumnMenuOpenId] = useState(null);

  useEffect(() => {
    if (!columnMenuOpenId) return;
    const handleClickOutside = () => setColumnMenuOpenId(null);
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [columnMenuOpenId]);

  const handleDeleteColumn = async (columnId) => {
    if (!window.confirm("Are you sure you want to delete this column and all its cards?")) return;
    setColumns((current) => current.filter((c) => c.id !== columnId));
    const tasksInColumn = tasks.filter(t => t.column_id === columnId);
    if (tasksInColumn.length > 0) {
      setTasks(current => current.filter(t => t.column_id !== columnId));
      const { error } = await supabase
        .from("project_tasks")
        .update({ is_archived: true, updated_at: new Date().toISOString() })
        .eq("column_id", columnId);
      if (error) {
        alert(error.message);
        loadBoard();
      }
    }
  };

  const loadBoard = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await fetchTasks(project.id);
      const normalized = (rows || []).map(normalizeTask).filter((task) => !task.is_archived);
      setTasks(normalized);
      setColumns((current) => applyStoredColumnLabels(project.id, mergeColumnsWithTasks(current, normalized)));
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  useEffect(() => {
    supabase
      .from("employees")
      .select("id, full_name, avatar_url, job_title")
      .eq("status", "Active")
      .order("full_name")
      .then(({ data }) => setTeamMembers(data || []));
  }, []);

  useEffect(() => {
    persistStoredColumnLabels(project.id, columns);
  }, [project.id, columns]);

  useEffect(() => {
    const channel = supabase
      .channel(`project_board_${project.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "project_tasks", filter: `project_id=eq.${project.id}` },
        () => {
          loadBoard();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [project.id, loadBoard]);

  useEffect(() => {
    if (!loading && activeTaskId && !tasks.some((task) => task.id === activeTaskId)) {
      setActiveTaskId(null);
    }
  }, [activeTaskId, loading, tasks]);

  const groupedTasks = useMemo(() => buildColumnMap(columns, tasks), [columns, tasks]);
  const taskCountsByColumn = useMemo(() => {
    const counts = {};
    columns.forEach((column) => {
      counts[column.id] = (groupedTasks[column.id] || []).length;
    });
    return counts;
  }, [columns, groupedTasks]);
  const activeTask = useMemo(() => tasks.find((task) => task.id === activeTaskId) || null, [activeTaskId, tasks]);

  const handleAddCard = async (columnId, title) => {
    const payload = {
      project_id: project.id,
      title,
      description: null,
      column_id: columnId,
      position: taskCountsByColumn[columnId] || 0,
      assigned_to: null,
      created_by: currentUser?.id || null,
      labels: [],
      checklist: [],
      due_date: null,
      member_ids: [],
      attachments: [],
      card_color: null,
      is_archived: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase.from("project_tasks").insert(payload).select("*").single();
    if (error) {
      alert(error.message);
      return;
    }

    const nextTask = normalizeTask(data);
    setTasks((current) => [...current, nextTask]);
    setAddingCardColumnId(null);
    setActiveTaskId(nextTask.id);
  };

  const handleAddList = (label) => {
    const nextIndex = columns.length % COLUMN_THEMES.length;
    const theme = COLUMN_THEMES[nextIndex];
    setColumns((current) => [
      ...current,
      {
        id: `list_${Date.now()}`,
        label,
        bg: theme.bg,
        text: theme.text,
        badgeText: theme.badgeText,
      },
    ]);
    setAddingList(false);
  };

  const startColumnRename = useCallback((column) => {
    setEditingColumnId(column.id);
    setColumnNameDraft(column.label);
  }, []);

  const saveColumnRename = useCallback(() => {
    if (!editingColumnId) return;
    const nextLabel = columnNameDraft.trim();
    if (!nextLabel) {
      setEditingColumnId(null);
      setColumnNameDraft("");
      return;
    }

    setColumns((current) => current.map((column) => (column.id === editingColumnId ? { ...column, label: nextLabel } : column)));
    setEditingColumnId(null);
    setColumnNameDraft("");
  }, [columnNameDraft, editingColumnId]);

  const handleTaskUpdate = useCallback((nextTask) => {
    const normalized = normalizeTask(nextTask);
    setTasks((current) => current.map((task) => (task.id === normalized.id ? normalized : task)));
  }, []);

  const handleTaskInserted = useCallback((nextTask) => {
    const normalized = normalizeTask(nextTask);
    setTasks((current) => {
      if (current.some((task) => task.id === normalized.id)) {
        return current;
      }
      return [...current, normalized];
    });
  }, []);

  const handleTaskArchived = useCallback((taskId) => {
    setTasks((current) => current.filter((task) => task.id !== taskId));
    setActiveTaskId((current) => (current === taskId ? null : current));
  }, []);

  const handleQuickDelete = useCallback(async (task) => {
    if (!window.confirm(`Delete "${task.title}"?`)) return;

    const previousTasks = tasks;
    handleTaskArchived(task.id);

    const { error } = await supabase
      .from("project_tasks")
      .update({ is_archived: true, updated_at: new Date().toISOString() })
      .eq("id", task.id);

    if (error) {
      setTasks(previousTasks);
      alert(error.message);
    }
  }, [handleTaskArchived, tasks]);

  const handleQuickCopy = useCallback(async (task) => {
    const payload = {
      project_id: task.project_id,
      title: `${task.title} (Copy)`,
      description: task.description || null,
      column_id: task.column_id,
      position: taskCountsByColumn[task.column_id] || 0,
      assigned_to: task.assigned_to || null,
      created_by: currentUser?.id || task.created_by || null,
      labels: task.labels || [],
      checklist: task.checklist || [],
      due_date: task.due_date || null,
      member_ids: task.member_ids || [],
      attachments: task.attachments || [],
      card_color: task.card_color || null,
      is_archived: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase.from("project_tasks").insert(payload).select("*").single();
    if (error) {
      alert(error.message);
      return;
    }

    handleTaskInserted(normalizeTask(data));
  }, [currentUser, handleTaskInserted, taskCountsByColumn]);

  const handleQuickMove = useCallback(async (task, targetColumnId) => {
    const previousTasks = tasks;
    const nextPosition = taskCountsByColumn[targetColumnId] || 0;
    const optimisticTask = normalizeTask({
      ...task,
      column_id: targetColumnId,
      position: nextPosition,
      updated_at: new Date().toISOString(),
    });

    handleTaskUpdate(optimisticTask);

    const { data, error } = await supabase
      .from("project_tasks")
      .update({
        column_id: targetColumnId,
        position: nextPosition,
        updated_at: new Date().toISOString(),
      })
      .eq("id", task.id)
      .select("*")
      .single();

    if (error) {
      setTasks(previousTasks);
      alert(error.message);
      return;
    }

    handleTaskUpdate(normalizeTask(data));
  }, [handleTaskUpdate, taskCountsByColumn, tasks]);

  const onDragEnd = async ({ source, destination }) => {
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const previousTasks = tasks;
    const reordered = reorderBoardTasks(tasks, source, destination);
    if (!reordered) return;

    setTasks(reordered.nextTasks);

    try {
      await Promise.all(
        reordered.changed.map(async (item) => {
          const { error } = await supabase
            .from("project_tasks")
            .update({
              column_id: item.column_id,
              position: item.position,
              updated_at: new Date().toISOString(),
            })
            .eq("id", item.id);
          if (error) throw error;
        }),
      );
    } catch (error) {
      setTasks(previousTasks);
      alert(error.message);
    }
  };

  if (loading) {
    return (
      <div style={{ width: "100%", minHeight: "420px", display: "flex", alignItems: "center", justifyContent: "center", ...FONT_STACK }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: "30px", height: "30px", borderRadius: "999px", border: `3px solid ${PRIMARY}`, borderTopColor: "transparent", margin: "0 auto 10px", animation: "taskboard-spin 0.8s linear infinite" }} />
          <p style={{ margin: 0, color: SUBTLE, fontSize: "13px" }}>Loading board...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes taskboard-spin { to { transform: rotate(360deg); } }
        .taskboard-scroll::-webkit-scrollbar {
          height: 8px;
        }
        .taskboard-scroll::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.05);
          border-radius: 8px;
        }
        .taskboard-scroll::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.15);
          border-radius: 8px;
        }
        .taskboard-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.25);
        }
      `}</style>

      <div
        style={{
          position: "relative",
          width: "100%",
          minHeight: "420px",
          overflow: "hidden",
          ...FONT_STACK,
        }}
      >
        <DragDropContext onDragEnd={onDragEnd}>
          <div
            className="taskboard-scroll"
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              overflowX: "auto",
              overflowY: "hidden",
              padding: "0 0 16px",
              minHeight: "420px",
            }}
          >
            {columns.map((column) => {
              const columnTasks = groupedTasks[column.id] || [];
              return (
                <section
                  key={column.id}
                  style={{
                    width: "272px",
                    minWidth: "272px",
                    background: column.bg,
                    borderRadius: "12px",
                    padding: "10px",
                    display: "flex",
                    flexDirection: "column",
                    alignSelf: "stretch",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", marginBottom: "10px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0, flex: 1 }}>
                      {editingColumnId === column.id ? (
                        <input
                          value={columnNameDraft}
                          onChange={(event) => setColumnNameDraft(event.target.value)}
                          onBlur={saveColumnRename}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              saveColumnRename();
                            }
                            if (event.key === "Escape") {
                              setEditingColumnId(null);
                              setColumnNameDraft("");
                            }
                          }}
                          autoFocus
                          style={{
                            flex: 1,
                            minWidth: 0,
                            height: "28px",
                            borderRadius: "6px",
                            border: `1px solid ${PRIMARY}`,
                            background: column.id === "todo" ? "rgba(255,255,255,0.95)" : "#fff",
                            color: TEXT,
                            padding: "0 8px",
                            boxSizing: "border-box",
                            outline: "none",
                            fontSize: "14px",
                            fontWeight: 600,
                            ...FONT_STACK,
                          }}
                        />
                      ) : (
                        <h2
                          onDoubleClick={() => startColumnRename(column)}
                          style={{ ...FONT_STACK, margin: 0, color: column.text, fontSize: "14px", fontWeight: 600, lineHeight: 1.3, minWidth: 0, cursor: "text" }}
                        >
                          {column.label}
                        </h2>
                      )}
                      <span style={{ padding: "2px 8px", borderRadius: "20px", background: "rgba(0,0,0,0.12)", color: column.badgeText || column.text, fontSize: "12px", fontWeight: 600, ...FONT_STACK }}>
                        {columnTasks.length}
                      </span>
                    </div>
                    <div style={{ position: "relative" }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setColumnMenuOpenId(columnMenuOpenId === column.id ? null : column.id);
                        }}
                        style={{ border: "none", background: "transparent", padding: "4px", borderRadius: "6px", color: column.text, lineHeight: 0, cursor: "pointer", opacity: 0.8 }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(0,0,0,0.08)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <MdMoreHoriz size={18} />
                      </button>
                      
                      {columnMenuOpenId === column.id && (
                        <div
                          onMouseDown={(e) => e.stopPropagation()}
                          style={{
                            position: "absolute",
                            right: 0,
                            top: "100%",
                            marginTop: "4px",
                            background: "#fff",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            zIndex: 30,
                            minWidth: "160px",
                            overflow: "hidden",
                            border: `1px solid ${BORDER}`,
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setColumnMenuOpenId(null);
                              startColumnRename(column);
                            }}
                            style={{ width: "100%", padding: "8px 12px", border: "none", background: "transparent", textAlign: "left", fontSize: "13px", color: TEXT, cursor: "pointer", ...FONT_STACK }}
                            onMouseEnter={e => e.currentTarget.style.background = "#f1f2f4"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                          >
                            Rename Column
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setColumnMenuOpenId(null);
                              setAddingCardColumnId(column.id);
                            }}
                            style={{ width: "100%", padding: "8px 12px", border: "none", background: "transparent", textAlign: "left", fontSize: "13px", color: TEXT, cursor: "pointer", ...FONT_STACK }}
                            onMouseEnter={e => e.currentTarget.style.background = "#f1f2f4"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                          >
                            Add Card
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setColumnMenuOpenId(null);
                              handleDeleteColumn(column.id);
                            }}
                            style={{ width: "100%", padding: "8px 12px", border: "none", background: "transparent", textAlign: "left", fontSize: "13px", color: "#e53935", cursor: "pointer", ...FONT_STACK }}
                            onMouseEnter={e => e.currentTarget.style.background = "#f1f2f4"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                          >
                            Delete Column
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <Droppable droppableId={column.id}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{
                          flex: 1,
                          minHeight: "50px",
                          borderRadius: "12px",
                          background: snapshot.isDraggingOver ? "rgba(9, 30, 66, 0.08)" : "transparent",
                          transition: "background 120ms ease",
                          display: "block",
                        }}
                      >
                        {columnTasks.map((task, index) => (
                          <TaskCard
                            key={task.id}
                            task={task}
                            index={index}
                            onOpenTask={setActiveTaskId}
                            teamMembers={teamMembers}
                            columnId={column.id}
                            columns={columns}
                            isLast={index === columnTasks.length - 1}
                            onDeleteCard={handleQuickDelete}
                            onCopyCard={handleQuickCopy}
                            onMoveCard={handleQuickMove}
                          />
                        ))}
                        {provided.placeholder}
                        {columnTasks.length === 0 && snapshot.isDraggingOver && (
                          <div style={{ padding: "12px", textAlign: "center", color: MUTED, fontSize: "13px", ...FONT_STACK }}>
                            Drop cards here
                          </div>
                        )}
                      </div>
                    )}
                  </Droppable>

                  <div style={{ marginTop: "10px" }}>
                    {addingCardColumnId === column.id ? (
                      <AddCardForm onAdd={(title) => handleAddCard(column.id, title)} onCancel={() => setAddingCardColumnId(null)} textColor={column.text} />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setAddingCardColumnId(column.id)}
                        onMouseEnter={(event) => {
                          event.currentTarget.style.background = "#e9e9e9";
                        }}
                        onMouseLeave={(event) => {
                          event.currentTarget.style.background = "transparent";
                        }}
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          border: "none",
                          borderRadius: "8px",
                          padding: "10px 12px",
                          background: "transparent",
                          color: "#5e6c84",
                          fontSize: "14px",
                          fontWeight: 500,
                          cursor: "pointer",
                          transition: "background 120ms ease",
                          ...FONT_STACK,
                        }}
                      >
                        <MdAdd size={18} />
                        Add a card
                      </button>
                    )}
                  </div>
                </section>
              );
            })}

            {addingList ? (
              <AddListForm onAdd={handleAddList} onCancel={() => setAddingList(false)} />
            ) : (
              <button
                type="button"
                onClick={() => setAddingList(true)}
                onMouseEnter={(event) => {
                  event.currentTarget.style.background = "rgba(0,0,0,0.1)";
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.background = "rgba(0,0,0,0.05)";
                }}
                style={{
                  width: "272px",
                  minWidth: "272px",
                  height: "48px",
                  alignSelf: "flex-start",
                  border: "none",
                  borderRadius: "12px",
                  padding: "0 16px",
                  background: "rgba(0,0,0,0.05)",
                  color: TEXT,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "background 120ms ease",
                  ...FONT_STACK,
                }}
              >
                <MdAdd size={18} />
                Add another list
              </button>
            )}
          </div>
        </DragDropContext>
      </div>

      {activeTask && (
        <TaskModal
          task={activeTask}
          columns={columns}
          currentUser={currentUser}
          taskCountsByColumn={taskCountsByColumn}
          onClose={() => setActiveTaskId(null)}
          onCardUpdate={handleTaskUpdate}
          onCardInserted={handleTaskInserted}
          onCardArchived={handleTaskArchived}
        />
      )}
    </>
  );
}
